import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, TrendingUp, Users, Target, Zap, BarChart3, Clock, Globe } from "lucide-react";
import heroImage from "@/assets/hero-partners.jpg";
import growthImage from "@/assets/growth-illustration.jpg";
import livestayLogo from "@/assets/live-stay-logo.png";

const PartnerLandingPage = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate("/cadastro-parceiro");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Header/Navigation */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <img 
            src={livestayLogo} 
            alt="Live Stay Logo" 
            className="h-10 md:h-12 cursor-pointer"
            onClick={() => navigate("/")}
          />
          <Button 
            variant="default" 
            size="lg"
            className="bg-orange-500 hover:bg-orange-600"
            onClick={handleGetStarted}
          >
            Começar Agora
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-orange-100 rounded-full border border-orange-200">
                <span className="text-orange-600 font-semibold text-sm">✨ Oferta Especial: Cadastro Gratuito</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold leading-tight">
                Alcance <span className="text-primary">Hóspedes</span> de Resorts
              </h2>
              <p className="text-xl text-muted-foreground">
                Divulgue seu estabelecimento para turistas hospedados em resorts da região. 
                Eles estão procurando exatamente o que você oferece!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  variant="default" 
                  size="lg" 
                  className="text-lg h-14 px-8 bg-orange-500 hover:bg-orange-600"
                  onClick={handleGetStarted}
                >
                  Comece Grátis Agora
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="text-lg h-14 px-8"
                  onClick={() => {
                    document.getElementById("como-funciona")?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Ver Como Funciona
                </Button>
              </div>
              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-600" />
                  <span className="text-sm">Cadastro gratuito</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-600" />
                  <span className="text-sm">Sem compromisso</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={heroImage} 
                  alt="Parceiros Live Stay crescendo juntos" 
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-card p-6 rounded-xl shadow-lg border border-border">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">Hóspedes</div>
                    <div className="text-sm text-muted-foreground">Procurando serviços</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold mb-4">
              Seu Negócio Visto por Quem Precisa
            </h3>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Turistas hospedados em resorts precisam de farmácia, restaurante, transfer, passeios e muito mais. 
              Esteja presente quando eles procurarem!
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="border-2 hover:border-primary/50 transition-all hover:shadow-lg">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <h4 className="text-xl font-bold">Hóspedes Precisando de Você</h4>
                <p className="text-muted-foreground">
                  Turistas em resorts precisam de farmácia, restaurante, transfer, passeios e serviços locais durante a estadia
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary/50 transition-all hover:shadow-lg">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Zap className="h-6 w-6 text-primary" />
                </div>
                <h4 className="text-xl font-bold">Clientes na Sua Porta</h4>
                <p className="text-muted-foreground">
                  Hóspedes veem seu estabelecimento no momento que precisam. Delivery, atendimento presencial ou agendamento direto
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary/50 transition-all hover:shadow-lg">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <BarChart3 className="h-6 w-6 text-primary" />
                </div>
                <h4 className="text-xl font-bold">Acompanhe Seus Resultados</h4>
                <p className="text-muted-foreground">
                  Veja quantas pessoas visualizaram seu estabelecimento e entraram em contato com você
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary/50 transition-all hover:shadow-lg">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h4 className="text-xl font-bold">Fácil de Usar</h4>
                <p className="text-muted-foreground">
                  Cadastre seu negócio em minutos. Sem complicação, sem burocracia. Nossa equipe te ajuda em tudo
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary/50 transition-all hover:shadow-lg">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Globe className="h-6 w-6 text-primary" />
                </div>
                <h4 className="text-xl font-bold">Diversos Resorts</h4>
                <p className="text-muted-foreground">
                  Alcance hóspedes de múltiplos resorts da sua região. Mais hospedagens = mais clientes para você
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary/50 transition-all hover:shadow-lg">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <h4 className="text-xl font-bold">Disponível 24/7</h4>
                <p className="text-muted-foreground">
                  Seu estabelecimento visível a qualquer hora. Hóspedes procuram serviços durante todo o dia
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-20 px-4 bg-card/30 backdrop-blur-sm">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={growthImage} 
                alt="Gráfico de crescimento de negócios" 
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>
            <div className="space-y-6">
              <h3 className="text-4xl font-bold">
                Perfeito Para Seu Tipo de Negócio
              </h3>
              <p className="text-lg text-muted-foreground">
                Se você tem um estabelecimento ou presta serviços para turistas, essa é a plataforma ideal para você!
              </p>
              
              <div className="space-y-4 pt-4">
                <div className="flex items-start gap-4">
                  <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-lg mb-1">Restaurantes, Padarias e Farmácias</h5>
                    <p className="text-muted-foreground">
                      Hóspedes precisam de refeições, compras básicas e medicamentos durante a estadia
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-lg mb-1">Transfer, Passeios e Fotógrafos</h5>
                    <p className="text-muted-foreground">
                      Turistas querem conhecer a região, fazer tours e registrar momentos especiais
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-lg mb-1">Salões, Massagem e Aluguel de Equipamentos</h5>
                    <p className="text-muted-foreground">
                      Serviços de bem-estar, beleza e equipamentos para lazer são sempre procurados
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="como-funciona" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold mb-4">
              É Muito Simples!
            </h3>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Cadastre seu estabelecimento e comece a receber clientes em 3 passos
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto text-2xl font-bold text-primary border-4 border-primary/20">
                1
              </div>
              <h4 className="text-2xl font-bold">Cadastre-se Grátis</h4>
              <p className="text-muted-foreground">
                Preencha os dados do seu estabelecimento. Leva menos de 5 minutos!
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto text-2xl font-bold text-primary border-4 border-primary/20">
                2
              </div>
              <h4 className="text-2xl font-bold">Adicione Seu Negócio</h4>
              <p className="text-muted-foreground">
                Fotos, telefone, endereço, horário e o que você oferece. Simples assim!
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto text-2xl font-bold text-primary border-4 border-primary/20">
                3
              </div>
              <h4 className="text-2xl font-bold">Receba os Hóspedes</h4>
              <p className="text-muted-foreground">
                Turistas veem seu negócio e entram em contato. Mais clientes para você!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <Card className="bg-gradient-to-br from-primary to-primary/80 border-0 shadow-xl">
            <CardContent className="p-12 text-center space-y-6">
              <div className="inline-block px-6 py-3 bg-background/10 backdrop-blur-sm rounded-full border border-background/20 mb-4">
                <span className="text-primary-foreground font-semibold">🎉 Oferta Especial</span>
              </div>
              <h3 className="text-4xl md:text-5xl font-bold text-primary-foreground">
                Cadastre-se Grátis Agora!
              </h3>
              <p className="text-xl text-primary-foreground/90 max-w-2xl mx-auto">
                Divulgue seu estabelecimento sem pagar nada. 
                Sem cartão de crédito. Sem compromisso. Veja os resultados antes de decidir!
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
                <Button 
                  variant="default" 
                  size="lg" 
                  className="text-lg h-14 px-12 bg-orange-500 hover:bg-orange-600"
                  onClick={handleGetStarted}
                >
                  Começar Meu Cadastro Grátis
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="text-lg h-14 px-12 bg-background/10 backdrop-blur-sm border-primary-foreground/20 text-primary-foreground hover:bg-background/20"
                  onClick={() => navigate("/fale-conosco")}
                >
                  Falar com Especialista
                </Button>
              </div>

              <div className="pt-8 flex flex-wrap justify-center gap-8 text-primary-foreground/80">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5" />
                  <span>Suporte completo</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5" />
                  <span>Sem taxas ocultas</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5" />
                  <span>Resultados garantidos</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card/50 backdrop-blur-sm border-t border-border/50 py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <div className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Live Stay. Todos os direitos reservados.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PartnerLandingPage;

